import React, { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import { Card } from "react-bootstrap";
import Footer from "../components/Footer";
import { useNavigate, useLocation } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getDocs, collection, doc, getDoc } from "firebase/firestore/lite";
import { db } from "../firebase/firebase-config";

const Home = () => {
  const aboutUsRef = useRef(null);
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [aboutUs, setAboutUs] = useState(null);
  const [sliders, setSliders] = useState([]); // ✅ dynamic sliders
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchAboutUs();
    fetchSliders();
  }, []);

  // ✅ Fetch sliders from Firebase (AdminPanel uploads here)
  async function fetchSliders() {
    try {
      const slidersCollectionRef = collection(
        db,
        "sacco",
        "TEKiK01inpRB3NFlK65S",
        "sliders"
      );
      const slidersSnapshot = await getDocs(slidersCollectionRef);
      const fetchedSliders = slidersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSliders(fetchedSliders);
    } catch (err) {
      console.error("Error fetching sliders: ", err.message);
    }
  }

  async function fetchProducts() {
    try {
      const productsCollectionRef = collection(
        db,
        "sacco",
        "TEKiK01inpRB3NFlK65S",
        "products"
      );
      const productsSnapshot = await getDocs(productsCollectionRef);
      const fetchedProducts = productsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(fetchedProducts);
    } catch (err) {
      console.error("Error fetching products: ", err.message);
    }
  }

  async function fetchAboutUs() {
    try {
      const aboutDocRef = doc(
        db,
        "sacco",
        "TEKiK01inpRB3NFlK65S",
        "settings",
        "aboutUs"
      );
      const aboutSnap = await getDoc(aboutDocRef);
      if (aboutSnap.exists()) setAboutUs(aboutSnap.data());
    } catch (err) {
      console.error("Error fetching About Us:", err.message);
    }
  }

  useEffect(() => {
    if (location.hash === "#about-us" && aboutUsRef.current) {
      aboutUsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // ✅ Slider settings (slower + right to left)
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 8000, // slower speed (8s per slide)
    rtl: true, // ✅ move right to left
  };

  const redirectToWhatsApp = (message) => {
    const phone = "254785278786";
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-100 flex flex-col items-center">
        <main className="flex-grow w-full max-w-7xl mx-auto px-6 py-16">
          {/* ✅ Dynamic Carousel Section */}
          <section className="mt-0">
            <Slider {...sliderSettings}>
              {sliders.length > 0 ? (
                sliders.map((slide) => (
                  <div key={slide.id} className="relative">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-[28rem] object-cover rounded-lg brightness-90"
                    />
                    {/* Overlay Text */}
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col justify-center items-center text-center text-white px-6">
                      <h2 className="text-3xl md:text-4xl font-extrabold drop-shadow-lg mb-3">
                        {slide.title}
                      </h2>
                      <p className="text-lg md:text-xl max-w-2xl drop-shadow-md">
                        {slide.description}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-20">
                  <p className="text-lg text-gray-600">
                    No sliders uploaded yet.
                  </p>
                </div>
              )}
            </Slider>
          </section>

          {/* Cards Section */}
          <section className="mt-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "fas fa-user-plus",
                  message: "I am interested in becoming a member.",
                  title: "Become a Member",
                  description: "Unlock exclusive benefits by joining us today.",
                },
                {
                  icon: "fas fa-hand-holding-usd",
                  message:
                    "I want to learn how to borrow funds with ease and flexibility.",
                  title: "Learn to Borrow",
                  description:
                    "Discover how to borrow funds easily and flexibly.",
                },
                {
                  icon: "fas fa-piggy-bank",
                  message: "I want to start saving and build my financial future.",
                  title: "Start Saving",
                  description:
                    "Build your financial future by starting to save with us.",
                },
                {
                  icon: "fas fa-cogs",
                  message:
                    "I am interested in exploring other financial services.",
                  title: "Explore Services",
                  description:
                    "Check out other financial services tailored for your needs.",
                },
              ].map((card, index) => (
                <div
                  key={index}
                  className="p-6 bg-white rounded-lg shadow-lg text-center cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:bg-teal-50"
                  onClick={() => redirectToWhatsApp(card.message)}
                >
                  <i className={`${card.icon} text-4xl text-teal-700 mb-4`}></i>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600">{card.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Dynamic About Us Section */}
          {aboutUs && (
            <section
              id="about-us-section"
              ref={aboutUsRef}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-800 rounded-lg shadow-xl p-8 mt-12 text-white"
            >
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="md:w-1/2">
                  <h2 className="text-4xl font-semibold mb-4">
                    {aboutUs.title || "About Us"}
                  </h2>
                  <p className="text-lg mb-4">{aboutUs.content}</p>
                  {aboutUs.vision && <p><b>Vision:</b> {aboutUs.vision}</p>}
                  {aboutUs.mission && <p><b>Mission:</b> {aboutUs.mission}</p>}
                  {aboutUs.coreValues && (
                    <p><b>Core Values:</b> {aboutUs.coreValues.join(", ")}</p>
                  )}
                  {aboutUs.quote && (
                    <p className="italic mt-2">"{aboutUs.quote}"</p>
                  )}
                </div>
                {aboutUs.image && (
                  <div className="md:w-1/2 flex justify-center items-center relative">
                    <img
                      src={aboutUs.image}
                      alt="About Us"
                      className="w-full h-auto rounded-lg shadow-lg transform transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Products Section */}
          <section className="mt-5">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Our Products
            </h2>

            {/* Loan Products */}
            <h3 className="text-2xl font-semibold text-gray-800 text-center mb-4">
              Various Loan Products
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products
                .filter((p) => p.productCategory === "Various Loan Products")
                .map((product) => (
                  <Card key={product.id} className="p-4 shadow-lg">
                    <Card.Img
                      variant="top"
                      src={product.image}
                      className="mb-4"
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                    <Card.Body>
                      <Card.Title className="font-bold">
                        {product.name}
                      </Card.Title>
                      <Card.Text>{product.details}</Card.Text>
                    </Card.Body>
                  </Card>
                ))}
            </div>

            {/* Saving Products */}
            <h3 className="text-2xl font-semibold text-gray-800 text-center mb-4 mt-8">
              Saving Products
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products
                .filter((p) => p.productCategory === "Saving Products")
                .map((product) => (
                  <Card key={product.id} className="p-4 shadow-lg">
                    <Card.Img
                      variant="top"
                      src={product.image}
                      className="mb-4"
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                    <Card.Body>
                      <Card.Title className="font-bold">
                        {product.name}
                      </Card.Title>
                      <Card.Text>{product.details}</Card.Text>
                    </Card.Body>
                  </Card>
                ))}
            </div>
          </section>

          {/* Loan Insurance */}
          <div className="mt-16 p-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-xl text-center max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">LOAN INSURANCE</h3>
            <p className="text-lg">
              All loans are insured against permanent disability and death of a
              member. Loans must be up to date. The original death certificate
              and burial permit must be accompanied during the claim process.
              Members are requested to update their next of kin regularly.
            </p>
          </div>
        </main>
      </div>

      {/* Map Section */}
      <div className="flex flex-col items-center">
        <div className="font-bold text-3xl">Find Us</div>
        <div className="my-map">
          <iframe
            title="Metropolitan Hospital Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15955.571479371347!2d36.8659665!3d-1.2874862!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1314c2f5e031%3A0x3906383134dd4dee!2sMetropolitan%20Hospital%20Sacco%20Limited!5e0!3m2!1sen!2ske!4v1709466367812!5m2!1sen!2ske"
            width="1200"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            frameBorder="0"
            aria-hidden="false"
            tabIndex="0"
          ></iframe>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Home;
